import { useState, useEffect, useMemo } from 'react';
import { auditAPI } from '@/services/api';
import TopBar from '@/components/layout/TopBar';
import { useLiveBots } from '@/hooks/useLiveBots';
import { useMarketData } from '@/hooks/useMarketData';
import { useForexRates } from '@/contexts/ForexRatesContext';
import './AuditPage.css';

interface AuditEntry {
  id?: string;
  timestamp?: string;
  user_email?: string;
  user_role?: string;
  action?: string;
  resource_type?: string;
  resource_id?: string;
  status?: string;
}

function fmtTime(iso?: string): string {
  if (!iso) return '--';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function AuditPage() {
  const { bots } = useLiveBots();
  const { indices, infraHealth } = useMarketData();
  const { rates } = useForexRates();
  const totalPnlAud = useMemo(() =>
    bots.reduce((s, b) => s + (b.performance?.todayPnl || 0) * (rates[b.market] || 1), 0), [bots, rates]);

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [error, setError] = useState(false);
  const [filterAction, setFilterAction] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    let mounted = true;
    async function fetch_() {
      try {
        const data = (await auditAPI.getRecent(200)) as AuditEntry[];
        if (mounted) { setEntries(data); setError(false); }
      } catch {
        if (mounted) setError(true);
      }
    }
    fetch_();
    const id = setInterval(fetch_, 30_000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const filtered = useMemo(() => {
    return entries.filter(e => {
      if (filterAction && e.action !== filterAction) return false;
      if (filterStatus && e.status !== filterStatus) return false;
      return true;
    });
  }, [entries, filterAction, filterStatus]);

  const actions = useMemo(() => [...new Set(entries.map(e => e.action).filter(Boolean))].sort(), [entries]);
  const statuses = useMemo(() => [...new Set(entries.map(e => e.status).filter(Boolean))].sort(), [entries]);

  return (
    <div className="audit-page">
      <TopBar totalPnlAud={totalPnlAud} syncStatus={infraHealth.syncStatus} indices={indices} />

      <div className="audit-page__body">
        <div className="audit-page__header">
          <h1 className="audit-page__title">Audit Trail</h1>
          <span className="audit-page__count">{filtered.length} entries</span>
        </div>

        <div className="audit-page__filters">
          <select value={filterAction} onChange={e => setFilterAction(e.target.value)}>
            <option value="">All Actions</option>
            {actions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {error ? (
          <div className="audit-page__error">Audit trail unavailable</div>
        ) : (
          <div className="audit-page__table-wrap">
            <table className="audit-page__table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Resource ID</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry, i) => (
                  <tr key={entry.id ?? i}>
                    <td>{fmtTime(entry.timestamp)}</td>
                    <td>{entry.user_email ?? '--'}</td>
                    <td>{entry.user_role ?? '--'}</td>
                    <td>{entry.action ?? '--'}</td>
                    <td>{entry.resource_type ?? '--'}</td>
                    <td className="audit-page__mono">{entry.resource_id ?? '--'}</td>
                    <td className={
                      entry.status === 'success' ? 'audit-page__status--success'
                        : entry.status === 'error' ? 'audit-page__status--error' : ''
                    }>
                      {entry.status ?? '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
